import { Component, effect, inject, input, linkedSignal, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { ImportService } from '@xanadu/service';
import { Register } from '@xanadu/core';
import { TreeSignal } from '@xanadu/signal';
import { Endpoint } from '@xanadu/model';
import { InputSubscriberComponent } from '../base/input-subscriber.component';
import { firstValueFrom } from 'rxjs';
import { EndpointCache } from '@xanadu/cache';

@Component({
    selector: 'app-endpoint',
    templateUrl: './endpoint.component.html',
    styleUrls: ['./endpoint.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Register('endpoint')
export class EndpointComponent extends InputSubscriberComponent {
    private readonly importService = inject(ImportService);
    private readonly treeSignal = inject(TreeSignal);
    private readonly endpointCache = inject(EndpointCache);

    endpointInput = input<Endpoint | undefined>(undefined, { alias: 'endpoint' });
    endpoint = linkedSignal<Endpoint | undefined>(() => this.endpointInput());

    metaEndpoint: any = { id: 'endpoint', name: 'Endpoint', info: 'Folder import endpoint', value: '_id', label: 'name' };
    metaContent: any = { id: 'content', name: 'Post content', rows: 12, textStyle: '{"font-family":"monospace"}' };
    metaResponse: any = { id: 'response', name: 'JSON response', rows: 18 };

    arrayImport: FormArray<any> = new FormArray<any>([]);

    parameters?: { meta: any, value: any }[];

    body?: string;

    formResponse: FormGroup = new FormGroup({});

    constructor() {
        super();

        effect(() => {
            const formGroup = this.formGroup();
            const model = this.model();

            if (formGroup && model) {
                const endpoints = this.endpointCache.signal();

                formGroup.addControl('parameters', this.arrayImport);

                this.metaEndpoint.options = endpoints ? [...endpoints.values()] : [];

                if (model?.endpoint) {
                    this.onEndpoint(model.endpoint);
                }
            }
        });

        effect(() => {
            const request = this.treeSignal.importRequest();
            const formGroup = this.formGroup();
            const endpoint = this.endpoint();

            if (formGroup && endpoint && request) {
                const entry = formGroup?.value;
                void firstValueFrom(this.importService.fetch(endpoint, entry.parameters, entry.content))
                .then(result => {
                    if (result) {
                        const response = JSON.stringify(result, null, 4);
                        this.formResponse.patchValue({ response });
                        this.treeSignal.importResponse.set(result);
                    }
                });
            }
        });

        effect(() => {
            const endpoints = this.endpointCache.signal();
            const endpoint = this.endpoint();

            if (this.metaEndpoint.options) {
                this.metaEndpoint.options.length = 0;
                if (endpoints) {
                    this.metaEndpoint.options.push(...endpoints.values());
                }
            }

            if (endpoint) {
                this.onEndpoint(endpoint._id);
            }
        });
    }

    public onEndpoint(event: any) {
        const endpoints = this.endpointCache.signal();

        if (endpoints) {
            this.endpoint.set(endpoints.get(event));
            const endpoint = this.endpoint();

            this.parameters = [];
            this.formResponse.patchValue({ response: '' });

            if (endpoint) {
                const model = this.model();

                const url: string = endpoint.data.url;
                const array = model?.parameters || [];
                const keys = url.match(/(?<=\[).+?(?=\])/g);

                keys?.forEach(key => {
                    const parameter = { meta: { label: { id: 'name', style: '{"width": "32%"}', info: 'Parameter', readonly: true }, value: { id: 'value', name: 'Value', style: '{"width": "64%"}' }, noDelete: true }, value: array.find((x: any) => x.name === key) || { name: key, value: '' } }
                    if (this.parameters) {
                        this.parameters.push(parameter);
                    }
                    this.arrayImport.push(
                        new FormGroup({}));
                });

                this.body = endpoint.data?.content;
                this.treeSignal.importEndpoint.set(endpoint);
            } else {
                this.body = undefined;
                this.treeSignal.importEndpoint.set(undefined);
            }
        }
    }

    public onJson(event: any) {
        if (this.formResponse.valid && !this.formResponse.pristine) {
            const obj = JSON.parse(event);
            this.treeSignal.importResponse.set(obj);
        } else {
            this.treeSignal.importResponse.set(undefined);
        }
    }
}
